import { forwardRef } from 'react';
import clsx from 'clsx';

// Base Field Container
interface FieldContainerProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  helpText?: string;
  children: React.ReactNode;
}

export function FieldContainer({
  label,
  htmlFor,
  required = false,
  error,
  helpText,
  children,
}: FieldContainerProps) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-text-primary"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {helpText && !error && (
        <p className="text-sm text-text-secondary">{helpText}</p>
      )}
    </div>
  );
}

// Text Input
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helpText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, required, className, ...props }, ref) => {
    return (
      <FieldContainer
        label={label}
        htmlFor={props.id || props.name || ''}
        required={required}
        error={error}
        helpText={helpText}
      >
        <input
          ref={ref}
          className={clsx(
            'w-full rounded-md border px-3 py-2 text-sm',
            'bg-surface-default text-text-primary placeholder:text-text-tertiary',
            'border-border-default focus:border-border-focus focus:ring-2 focus:ring-border-focus/20',
            'transition-colors',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            className
          )}
          {...props}
        />
      </FieldContainer>
    );
  }
);

Input.displayName = 'Input';

// Textarea
interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  helpText?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helpText, required, className, rows = 3, ...props }, ref) => {
    return (
      <FieldContainer
        label={label}
        htmlFor={props.id || props.name || ''}
        required={required}
        error={error}
        helpText={helpText}
      >
        <textarea
          ref={ref}
          rows={rows}
          className={clsx(
            'w-full rounded-md border px-3 py-2 text-sm',
            'bg-surface-default text-text-primary placeholder:text-text-tertiary',
            'border-border-default focus:border-border-focus focus:ring-2 focus:ring-border-focus/20',
            'transition-colors resize-none',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            className
          )}
          {...props}
        />
      </FieldContainer>
    );
  }
);

Textarea.displayName = 'Textarea';

// Select
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  error?: string;
  helpText?: string;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      error,
      helpText,
      required,
      placeholder,
      className,
      ...props
    },
    ref
  ) => {
    // Always show a placeholder to prevent empty selection bugs
    const displayPlaceholder = placeholder ?? `Select ${label.toLowerCase()}`;

    return (
      <FieldContainer
        label={label}
        htmlFor={props.id || props.name || ''}
        required={required}
        error={error}
        helpText={helpText}
      >
        <select
          ref={ref}
          className={clsx(
            'w-full rounded-md border px-3 py-2 text-sm',
            'bg-surface-default text-text-primary',
            'border-border-default focus:border-border-focus focus:ring-2 focus:ring-border-focus/20',
            'transition-colors cursor-pointer',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
            className
          )}
          {...props}
        >
          <option value="" disabled>
            {displayPlaceholder}
          </option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
      </FieldContainer>
    );
  }
);

Select.displayName = 'Select';

// Checkbox
interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  description?: string;
  helpText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, helpText, className, ...props }, ref) => {
    const displayText = helpText || description;
    return (
      <div className="flex items-start gap-3">
        <input
          ref={ref}
          type="checkbox"
          className={clsx(
            'mt-0.5 h-4 w-4 rounded border-border-default text-primary-default',
            'focus:ring-2 focus:ring-border-focus focus:ring-offset-0',
            'transition-colors cursor-pointer',
            className
          )}
          {...props}
        />
        <div className="flex-1">
          <label
            htmlFor={props.id || props.name}
            className="block text-sm font-medium text-text-primary cursor-pointer"
          >
            {label}
          </label>
          {displayText && (
            <p className="text-sm text-text-secondary mt-0.5">{displayText}</p>
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
